/**
 * GeoMap — world coverage view of the knowledge graph.
 *
 * Two layers, fetched together from /api/kg/places/geo:
 *   • Country bubbles — external_records aggregated by country_code, plotted
 *     at country centroids. Sized by record count. This is where the bulk
 *     of the KG (10M+) actually lives.
 *   • Place dots — geocoded kg_places, clustered with leaflet.markercluster,
 *     sized by mention count. Finer grained but lower coverage.
 *
 * Year slider scopes both layers (kg_mentions.event_year for places,
 * external_records.birth_year for countries). Event-type filter applies
 * to places only.
 *
 * Plain Leaflet (not react-leaflet) — matches the existing dep set.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { api } from '../../services/api.js'

const EVENT_TYPES = [
  { id: '',                  label: 'All events' },
  { id: 'birth',             label: 'Birth' },
  { id: 'death',             label: 'Death' },
  { id: 'marriage',          label: 'Marriage' },
  { id: 'document_origin',   label: 'Document origin' },
]

// Place radius — log scale so 1000 mentions don't dwarf 1 mention.
function placeRadius(count) {
  return 4 + Math.min(14, Math.log2(Math.max(1, count)) * 2)
}

// Country radius — sqrt(count) scaled so US (8M) and BE (10k) both legible.
// At max we want ~80px, at min ~12px.
function countryRadius(count, maxCount) {
  if (!maxCount) return 12
  const norm = Math.sqrt(count) / Math.sqrt(maxCount)
  return 12 + norm * 68
}

function formatCount(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`
  return String(n)
}

export default function GeoMap() {
  const mapEl = useRef(null)
  const mapRef = useRef(null)
  const placesLayerRef = useRef(null)
  const countriesLayerRef = useRef(null)

  const [range, setRange] = useState({ minYear: 1500, maxYear: new Date().getFullYear() })
  const [filter, setFilter] = useState({ minYear: null, maxYear: null, eventType: '' })
  const [data, setData] = useState({ countries: [], places: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // ── Init map once ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return

    const map = L.map(mapEl.current, {
      worldCopyJump: true,
      minZoom: 2,
    }).setView([30, 10], 2)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map)

    const countriesLayer = L.layerGroup().addTo(map)
    const placesLayer = L.markerClusterGroup({
      chunkedLoading: true,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 50,
    })
    map.addLayer(placesLayer)

    mapRef.current = map
    countriesLayerRef.current = countriesLayer
    placesLayerRef.current = placesLayer

    return () => {
      map.remove()
      mapRef.current = null
      countriesLayerRef.current = null
      placesLayerRef.current = null
    }
  }, [])

  // ── Load year range once ───────────────────────────────────────────────
  useEffect(() => {
    api.get('/kg/places/geo/range').then(r => {
      setRange({ minYear: r.minYear, maxYear: r.maxYear })
    }).catch(() => {})
  }, [])

  // ── Fetch points whenever filter changes ───────────────────────────────
  useEffect(() => {
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (filter.minYear !== null)   params.set('minYear', filter.minYear)
    if (filter.maxYear !== null)   params.set('maxYear', filter.maxYear)
    if (filter.eventType)          params.set('eventType', filter.eventType)
    const qs = params.toString()
    api.get(`/kg/places/geo${qs ? `?${qs}` : ''}`)
      .then(r => setData({ countries: r.countries || [], places: r.places || [] }))
      .catch(err => setError(err.message || 'Failed to load coverage'))
      .finally(() => setLoading(false))
  }, [filter])

  // ── Render country bubbles ─────────────────────────────────────────────
  useEffect(() => {
    const layer = countriesLayerRef.current
    if (!layer) return
    layer.clearLayers()
    const maxCount = data.countries.reduce((m, c) => Math.max(m, c.count), 0)
    for (const c of data.countries) {
      const m = L.circleMarker([c.lat, c.lng], {
        radius: countryRadius(c.count, maxCount),
        color: '#b45309',
        weight: 2,
        fillColor: '#f59e0b',
        fillOpacity: 0.45,
      })
      m.bindTooltip(
        `<strong>${c.country_code}</strong>: ${c.count.toLocaleString()} records`,
        { permanent: false, direction: 'top' }
      )
      // Inline label for the big ones — readable at world zoom.
      if (c.count >= 10_000) {
        const label = L.divIcon({
          className: 'world-map__country-label',
          html: `<div>${c.country_code}<br/><small>${formatCount(c.count)}</small></div>`,
          iconSize: [60, 32],
          iconAnchor: [30, 16],
        })
        L.marker([c.lat, c.lng], { icon: label, interactive: false }).addTo(layer)
      }
      m.addTo(layer)
    }
  }, [data.countries])

  // ── Render place dots ──────────────────────────────────────────────────
  useEffect(() => {
    const cluster = placesLayerRef.current
    if (!cluster) return
    cluster.clearLayers()
    const markers = data.places.map(p => {
      const m = L.circleMarker([p.lat, p.lng], {
        radius: placeRadius(p.mention_count),
        color: '#1f4f8b',
        weight: 1,
        fillColor: '#3b82f6',
        fillOpacity: 0.65,
      })
      m.bindPopup(`
        <strong>${escapeHtml(p.name)}</strong><br/>
        ${p.country_code ? `<small>${escapeHtml(p.country_code)}</small><br/>` : ''}
        <small>${p.mention_count} mention${p.mention_count === 1 ? '' : 's'}</small>
      `)
      return m
    })
    cluster.addLayers(markers)
  }, [data.places])

  // ── Stats ──────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const countryTotal = data.countries.reduce((s, c) => s + c.count, 0)
    return {
      countryCount: data.countries.length,
      countryTotal,
      placeCount: data.places.length,
    }
  }, [data])

  const sliderMin = filter.minYear ?? range.minYear
  const sliderMax = filter.maxYear ?? range.maxYear

  return (
    <div className="world-map">
      <div className="world-map__controls">
        <div className="world-map__filters">
          <label className="world-map__field">
            <span>Event (places only)</span>
            <select
              value={filter.eventType}
              onChange={e => setFilter(f => ({ ...f, eventType: e.target.value }))}
            >
              {EVENT_TYPES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>

          <div className="world-map__years">
            <span className="world-map__years-label">
              Years: <strong>{sliderMin}</strong> — <strong>{sliderMax}</strong>
            </span>
            <div className="world-map__slider">
              <input
                type="range"
                min={range.minYear}
                max={range.maxYear}
                value={sliderMin}
                onChange={e => {
                  const v = Math.min(parseInt(e.target.value, 10), sliderMax)
                  setFilter(f => ({ ...f, minYear: v }))
                }}
              />
              <input
                type="range"
                min={range.minYear}
                max={range.maxYear}
                value={sliderMax}
                onChange={e => {
                  const v = Math.max(parseInt(e.target.value, 10), sliderMin)
                  setFilter(f => ({ ...f, maxYear: v }))
                }}
              />
            </div>
            {(filter.minYear !== null || filter.maxYear !== null || filter.eventType) && (
              <button
                className="world-map__reset"
                onClick={() => setFilter({ minYear: null, maxYear: null, eventType: '' })}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <div className="world-map__stats">
          {loading
            ? 'Loading…'
            : error
              ? <span className="world-map__error">{error}</span>
              : (
                <>
                  <span className="world-map__stat world-map__stat--country">
                    {stats.countryTotal.toLocaleString()} records · {stats.countryCount} countries
                  </span>
                  <span className="world-map__stat world-map__stat--place">
                    {stats.placeCount.toLocaleString()} geocoded places
                  </span>
                </>
              )}
        </div>
      </div>

      <div ref={mapEl} className="world-map__canvas" />

      <div className="world-map__legend">
        <span><span className="world-map__swatch world-map__swatch--country" /> Country totals (external_records)</span>
        <span><span className="world-map__swatch world-map__swatch--place" /> Geocoded places (kg_places)</span>
      </div>
    </div>
  )
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
